import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button } from '@/components/ui';
import { getSitemapRegistry, backfillSitemapRegistry, type SitemapBackfillResult, type SitemapParity } from '@/data/client';
import { apiErrMsg } from './util';

/* The site-map registry next to the legacy sitemap blob, for the migration.
   Shows whether the table agrees with the blob the way the generators read it
   (path + anchor per page) and runs the copy. Nothing reads the table until
   the readers are switched, so the button is safe on any client, any number
   of times; a re-run on an unchanged client reports "unchanged". */
export function SitemapRegistryPanel({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const key = ['admin', 'sitemap-registry', orgId];
  const { data, isLoading, error } = useQuery({ queryKey: key, queryFn: () => getSitemapRegistry(orgId) });
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<SitemapBackfillResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await backfillSitemapRegistry(orgId);
      setLast(r.result);
      qc.setQueryData(key, { parity: r.parity });
    } catch (e) { setErr(apiErrMsg(e, 'Could not migrate the sitemap.')); }
    finally { setBusy(false); }
  };

  const p: SitemapParity | undefined = data?.parity;
  const clean = !!p && p.hasRegistry && p.missing.length === 0 && p.anchorDiffs.length === 0 && p.hostDiffs.length === 0 && p.focusMatches;

  return (
    <section className="admin-sect">
      <h4 className="admin-sect__title">Site map registry</h4>
      <p className="dash-muted admin-sect__note">
        Copies the Vault's sitemap, the SEO focus pages and Page Health's routes into one table, page by page. Nothing reads the table yet; this only prepares it.
      </p>
      {isLoading && <p className="dash-muted">Checking…</p>}
      {error && <p className="dash-muted">{apiErrMsg(error, 'Could not load the registry state.')}</p>}
      {p && (
        <div className="dash-stack" style={{ gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {!p.hasBlob && !p.hasRegistry && <Badge variant="neutral">No sitemap</Badge>}
            {p.hasBlob && !p.hasRegistry && <Badge variant="neutral">Not migrated</Badge>}
            {p.hasRegistry && (clean ? <Badge variant="success" dot>Matches the Vault</Badge> : <Badge variant="warning">Differs from the Vault</Badge>)}
            <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>
              Vault: {p.legacyPages} page{p.legacyPages === 1 ? '' : 's'} in {p.legacyRows} source{p.legacyRows === 1 ? '' : 's'} · Registry: {p.registryPages}
              {Object.keys(p.byStatus).length ? ` (${Object.entries(p.byStatus).map(([k, v]) => `${v} ${k}`).join(', ')})` : ''}
              {p.hasRegistry ? ` · ${p.titled} titled, ${p.briefed} with a brief` : ''}
            </span>
          </div>
          <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>
            Links are built on {p.origin || 'no origin — set the website in Profile'}
            {p.stagingUrl ? ` (staging; the profile's site is ${p.bizWebsite || 'unset'})` : ''}.
          </span>
          {p.hasRegistry && p.missing.length > 0 && (
            <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>Missing from the registry: {p.missing.join(', ')}</span>
          )}
          {p.hasRegistry && p.anchorDiffs.length > 0 && (
            <details>
              <summary className="dash-muted" style={{ fontSize: 'var(--text-xs)', cursor: 'pointer' }}>{p.anchorDiffs.length} link line{p.anchorDiffs.length === 1 ? '' : 's'} differ</summary>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 'var(--text-xs)' }}>
                {p.anchorDiffs.slice(0, 20).map((d) => <li key={d.route}><code>{d.legacy}</code> → <code>{d.registry}</code></li>)}
              </ul>
            </details>
          )}
          {p.hasRegistry && p.hostDiffs.length > 0 && (
            <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{p.hostDiffs.length} page{p.hostDiffs.length === 1 ? '' : 's'} on a different host ({p.hostDiffs[0].legacy} in the Vault, {p.hostDiffs[0].registry} here).</span>
          )}
          {p.hasRegistry && !p.focusMatches && (
            <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>The SEO focus pages differ from the Profile — re-run the migration after saving the Profile.</span>
          )}
          {p.hasRegistry && p.extra.length > 0 && (
            <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{p.extra.length} page{p.extra.length === 1 ? '' : 's'} beyond the Vault's sitemap (focus pages or scanned pages): {p.extra.slice(0, 8).join(', ')}{p.extra.length > 8 ? '…' : ''}</span>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Button size="sm" variant="outline" disabled={busy || (!p.hasBlob && !p.bizWebsite)} onClick={run}>
              {busy ? 'Migrating…' : p.hasRegistry ? 'Re-run migration' : 'Migrate sitemap'}
            </Button>
            {last && (
              <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>
                {last.inserted} added, {last.updated} updated, {last.unchanged} unchanged · {last.total} pages{last.originNote ? ` · ${last.originNote}` : ''}
              </span>
            )}
            {err && <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{err}</span>}
          </div>
        </div>
      )}
    </section>
  );
}
